<template>
  <div class="tasks-logs">
    <OmxList
        :data="tasksLogsData"
        :loading="isLoading"
        :show-search="false"
        empty-icon="format-list-checks"
    >
      <template #actions v-if="currentUserAccess('ProjectLogs', UserActions.CREATE, project_id)">
        <b-button type="is-primary" icon-left="plus-thick" @click="addNewTaskLogClicked">
          {{ $t('tasks-logs.new-task-log-button') }}
        </b-button>
      </template>

      <b-table-column field="date" :label="$t('tasks-logs.date-column')" v-slot="props">
        {{ props.row.date }}
      </b-table-column>
      <b-table-column field="title" :label="$t('tasks-logs.title-column')" v-slot="props">
        {{ props.row.title }}
      </b-table-column>
      <b-table-column field="duration_internal" :label="$t('tasks-logs.duration-internal-column')" v-slot="props">
        {{ getFormattedTimeHour(props.row.duration_internal) }}
      </b-table-column>
      <b-table-column field="duration_external" :label="$t('tasks-logs.duration-external-column')" v-slot="props">
        {{ getFormattedTimeHour(props.row.duration_external) }}
      </b-table-column>
      <b-table-column field="quantity" :label="$t('tasks-logs.quantity-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.quantity, props.row.unit) }}
      </b-table-column>
      <b-table-column field="logged_expected_quantity" :label="$t('tasks-logs.logged-expected-quantity-column')" v-slot="props">
        {{ getFormattedQuantityValue(props.row.logged_expected_quantity, props.row.unit) }}
      </b-table-column>
      <b-table-column field="logged_executed_performance" :label="$t('tasks-logs.logged-executed-performance-column')" v-slot="props">
        {{ getFormattedPerformance(props.row.logged_executed_performance, props.row.unit) }}
      </b-table-column>
      <b-table-column field="profit" :label="$t('tasks-logs.profit-column')" v-slot="props">
        <p :style="profitTextStyle(props.row.profit)">{{ getFormattedPricing(props.row.profit) }}</p>
      </b-table-column>
      <b-table-column
          v-if="currentUserAccess('ProjectLogs', UserActions.DELETE, project_id)"
          :label="$t('tasks-logs.actions-column')" centered v-slot="props" width="10%">
        <b-tooltip :label="$t('common.edit')" position="is-top">
          <b-button type="is-primary" size="is-small" class="m-1" icon-left="pencil"
                    @click="editTaskLogClicked(props.row.taskLog)" />
        </b-tooltip>
        <b-tooltip :label="$t('common.delete')" position="is-top">
          <b-button type="is-danger" size="is-small" class="m-1" icon-left="delete"
                    @click="deleteTaskLogClicked(props.row.taskLog)" />
        </b-tooltip>
      </b-table-column>
    </OmxList>

    <b-modal
        v-model="isTaskLogModalActive"
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal>
      <template #default="props">
        <TaskLog :project_id="project_id" :tasks_data="tasksData" :log_data="selected_log" :selected_date="selected_date"
                 @close="props.close"
                 @taskLogAdded="readTasksLogsData" @taskLogUpdated="readTasksLogsData" />
      </template>
    </b-modal>
  </div>
</template>

<script>

import firebase from "firebase/app";
import TaskLog from "@/components/TaskLog";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "TasksLogs",
  components: {
    TaskLog,
    OmxList
  },
  props: {
    'project_id': {
      type: String,
    },
    'selected_date': {},
  },
  data() {
    return {
      modelDate: new Date(),
      selectedUntilDate: new Date((new Date()).setHours(23, 59, 59, 0)),
      selectedFromDate: new Date((new Date()).setHours(0, 0, 0, 0)),
      tasksData: [],
      tasksLogsData: [],
      isLoading: false,
      isTaskLogModalActive: false,
      selected_log: {}
    }
  },
  watch: {
    selected_date: function () {
      this.dateSelectClicked(this.selected_date)
    }
  },
  methods: {
    readTasksData: function () {
      this.tasksData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getTasksFunctions = functions.httpsCallable(this.getSubmitEndpoint());
      getTasksFunctions({action: "get_tasks", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            if (result.data.tasks.length > 0) {
              result.data["tasks"].forEach((task) => {
                this.tasksData.push({
                  task: task,
                  title: task.title
                });
              });
            }
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting tasks:", error?.code);
          });
    },
    readTasksLogsData: function () {
      this.isTaskLogModalActive = false;
      this.isLoading = true;
      this.tasksLogsData = [];
      this.selected_log = {};
      this.selected_log.document_id = "new"
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getTasksLogsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payloadData = {
        action: "get_logs",
        project_id: this.project_id,
        task_id: "*",
        fill_meta: true,
        filters: [['date', '>=', this.selectedFromDate.toISOString()], ['date', '<', this.selectedUntilDate.toISOString()]]
      }
      getTasksLogsFunction(payloadData)
          .then((result) => {
            if (result.data.taskslogs.length > 0) {
              result.data["taskslogs"].forEach((taskLog) => {
                this.tasksLogsData.push({
                  taskLog: taskLog,
                  title: taskLog.parent.title,
                  duration_internal: taskLog.duration_internal,
                  duration_external: taskLog.duration_external,
                  quantity: taskLog.quantity,
                  unit: taskLog.parent.unit,
                  logged_executed_performance: taskLog.meta.executed_performance,
                  logged_expected_quantity: taskLog.meta.expected_quantity,
                  profit: taskLog.meta.profit,
                  date: this.getFormattedDate(taskLog.date),
                  document_id: taskLog.document_id
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message)
            console.log("Error getting tasks logs:", error?.code);
          });
    },
    deleteTaskWorklog: function (taskLog) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteTaskLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteTaskLogFunction({
        action: "delete_log",
        project_id: taskLog.project_id,
        task_id: taskLog.task_id,
        document_id: taskLog.document_id,
      }).then(() => {
        this.isLoading = false;
        this.showSuccess(this.$t('messages.task-log-deleted'));
        this.readTasksLogsData()
      }).catch((error) => {
        this.showError(this.$t('messages.save-error'))
        console.error("Error deleting task log:", error?.code);
        this.isLoading = false;
      });
    },
    /*
     UI Events
     */
    dateSelectClicked: function (date) {
      const fromDate = date.setHours(0, 0, 0, 0)
      const untilDate = date.setHours(23, 59, 59, 0)
      this.selectedFromDate = new Date(fromDate)
      this.selectedUntilDate = new Date(untilDate)
      this.readTasksLogsData();
    },
    addNewTaskLogClicked: function () {
      this.readTasksData();
      this.selected_log = {};
      this.selected_log.document_id = "new";
      this.isTaskLogModalActive = true;
    },
    editTaskLogClicked: function (taskLog) {
      this.readTasksData();
      this.selected_log = taskLog;
      this.isTaskLogModalActive = true;
    },
    async deleteTaskLogClicked(taskLog) {
      const confirmed = await this.$omxConfirm({
        title: this.$t('tasks-logs.delete-log-title').toString(),
        message: this.$t('tasks-logs.delete-log-message').toString(),
        confirmText: this.$t('tasks-logs.delete-log-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        icon: 'delete',
        type: 'is-danger'
      });
      if (!confirmed) return;
      this.deleteTaskWorklog(taskLog);
      this.readTasksData();
    }
  },
  mounted() {
    this.readTasksLogsData();
  }
};
</script>

<style scoped>
.tasks-logs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
